import React, { FC } from 'react';
import Icon from '@minecraft.icon';
import { BodyText, BodyTextSmall, FlexCol, FlexDynamic, H2Text } from '@minecraft.atoms';
import { dateFormat, getDate } from '@minecraft.date-utils';
import { useTranslation } from '@blocs.i18n';
import { gqlAccount, gqlCountry, gqlCurrencyEnum } from '@minecraft.graphql-types';
import { PlanType } from '../usePlans';
import { TemperatureValues } from '../enums/plans';
import { UsePayment } from '../types';
import { priceFormatter } from '../helpers/priceFormatter';
import { OrderSuccessWrapperProps } from './types';

interface SuccessHeroProps {
  isModal: boolean;
  selectedPlan: PlanType;
  lessMd?: boolean;
  completedPurchase: UsePayment['completedPurchase'];
  subscription?: OrderSuccessWrapperProps['subscription'];
  currencyCode: gqlAccount['country']['currency']['code'] | gqlCurrencyEnum;
  countryCode?: gqlCountry['code'];
}

const BackgroundWrapper: FC<{
  className?: string;
  temperature: TemperatureValues;
  backgroundImage?: string;
}> = ({ children, className = '', temperature, backgroundImage }) => {
  return backgroundImage ? (
    <FlexCol
      w="full"
      m="auto"
      justifyContent="center"
      alignItems="center"
      h="full"
      backgroundImage={{
        image: backgroundImage,
        size: 'cover',
        position: 'center',
        repeat: 'no-repeat',
      }}
    >
      <FlexDynamic
        w="full"
        m="auto"
        justifyContent="center"
        alignItems="center"
        h="full"
        bgColor="maxContrast"
        bgOpacity={0.8}
        zIndex={4}
        className={className}
        mdProps={{ className: 'cn_atom_px-8' }}
      >
        {children}
      </FlexDynamic>
    </FlexCol>
  ) : (
    <FlexCol
      justifyContent="center"
      alignItems="center"
      bgColor={`${temperature}PaywallSuccessBg`}
      maxW="full"
      w="full"
      className={className}
    >
      {children}
    </FlexCol>
  );
};

export const SuccessHero: FC<SuccessHeroProps> = React.memo(
  ({ selectedPlan, completedPurchase, subscription, currencyCode, countryCode }) => {
    const translationCode =
      selectedPlan?.translationCode ?? completedPurchase?.subscription?.plan?.billingCode ?? selectedPlan?.billingCode;
    const { t } = useTranslation();
    const temperature = selectedPlan?.temperature || TemperatureValues.vanilla;
    const isTrialPlan = Boolean(selectedPlan?.trialLengthDays && completedPurchase?.subscription?.trialEndsAt);
    const nextBillingDate = getDate(
      completedPurchase?.subscription?.nextBillingDate ?? subscription?.nextBillingDate,
      dateFormat.MONTH_DAY_YEAR
    );
    const price = priceFormatter({
      price: completedPurchase?.subscription?.plan?.price ?? completedPurchase?.total,
      currencyCode: completedPurchase?.currency?.code ?? currencyCode,
      countryCode,
      t,
    });

    return (
      <BackgroundWrapper
        temperature={temperature}
        className={`cn_gradient-bg-${selectedPlan?.gradientColor} cn_atom_px-0 cn_atom_py-4`}
        backgroundImage={selectedPlan?.backgroundImage}
      >
        <FlexDynamic
          bgColor="card"
          gap="2"
          justifyContent="center"
          alignItems="center"
          borderRadius="sm"
          className="cn_atom_p-6"
          w="full"
          maxW="md"
          my="8"
          mx="0"
          mdProps={{ m: '8' }}
        >
          <FlexCol justifyContent="center" alignItems="center" w="full">
            <Icon name={selectedPlan?.heroImage} />
          </FlexCol>
          <FlexDynamic
            direction="column"
            gap="0"
            mdProps={{ direction: 'row', gap: '2', justifyContent: 'center' }}
            wrap="nowrap"
            justifyContent="center"
            alignItems="center"
          >
            <Icon name={selectedPlan?.iconName || 'check-circle'} themeColor={`${temperature}Paywall`} />
            <H2Text fontWeight="medium" lineHeight="expanded" className="cn_text-center cn_atom_px-0" align="center">
              {isTrialPlan
                ? t(`common:paywall.success.trial.title.${translationCode}`, {
                    trialLengthDays: selectedPlan?.trialLengthDays ?? 30,
                  })
                : t(`common:paywall.success.title.${translationCode}`)}
            </H2Text>
          </FlexDynamic>
          <BodyText fontWeight="medium" lineHeight="expanded" align="center">
            {t(
              isTrialPlan
                ? `common:paywall.success.trial.date.${translationCode}`
                : `common:paywall.success.date.${translationCode}`,
              {
                date: getDate(
                  completedPurchase?.subscription?.expirationDate ?? subscription?.expirationDate,
                  dateFormat.MONTH_DAY_YEAR
                ),
              }
            )}
          </BodyText>
          {isTrialPlan && (
            <FlexCol justifyContent="center" alignItems="center" mt="2">
              <BodyTextSmall fontWeight="medium" align="center" color="disabled">
                {t('common:paywall.success.trial.paymentDate', { price, nextBillingDate })}
              </BodyTextSmall>
            </FlexCol>
          )}
        </FlexDynamic>
      </BackgroundWrapper>
    );
  }
);
